import { Badge, Tooltip } from 'antd'

import { useFormatMessage } from '../../helpers/intl'
import { Transaction, Transactionv2 } from '../../models/transaction'
import { V3Transaction, Payment } from '../../models/v3-transaction'
import { theme } from '../../theme'

interface props {
  transaction: Transaction
}

export const TransactionStateBadge: React.FC<props> = ({ transaction }) => {
  const [status, backgroundColor, fontColor] = getStyle(transaction.currentState)

  const translatedStatus = useFormatMessage(`dashboard.transaction.${status}`, formatV3StateName(status))

  return (
    <Badge
      count={translatedStatus}
      style={{ backgroundColor, color: fontColor, boxShadow: `${backgroundColor} 0px 0px 0px 0.5px` }}
    />
  )
}

interface propsV2 {
  transaction: Transactionv2
}

export const TransactionStateBadgeV2: React.FC<propsV2> = ({ transaction }) => {
  const [status, backgroundColor, fontColor] = getStyle(transaction.state)

  const translatedStatus = useFormatMessage(`dashboard.transaction.${status}`, formatV3StateName(status))

  return (
    <Badge
      count={translatedStatus}
      style={{ backgroundColor, color: fontColor, boxShadow: `${backgroundColor} 0px 0px 0px 0.5px` }}
    />
  )
}

interface propsV3 {
  transaction: V3Transaction
}

/**
 * Format snake_case state to Title Case
 * Example: failed_initiation -> Failed Initiation
 */
const formatV3StateName = (state: string): string => {
  return state
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const normalizeV3State = (state: string): string => {
  return state.trim().toLowerCase().replace(/[\s-]+/g, '_')
}

export const V3TransactionStateBadge: React.FC<propsV3> = ({ transaction }) => {
  const normalizedState = normalizeV3State(transaction.currentState || '')
  const [status, backgroundColor, fontColor] = getV3Style(normalizedState)
  const translatedStatus = useFormatMessage(`dashboard.transaction.${status}`, formatV3StateName(status))
  const badge = (
    <Badge
      count={translatedStatus}
      style={{ backgroundColor, color: fontColor, boxShadow: `${backgroundColor} 0px 0px 0px 0.5px` }}
    />
  )
  const errorMsg = (transaction.error_code_message ?? transaction.errorCodeMessage)?.trim()
  if (errorMsg) return <Tooltip title={errorMsg} overlayInnerStyle={{ textAlign: 'center', maxWidth: 200 }}>{badge}</Tooltip>
  return badge
}

const paymentStateDisplayNames: Record<string, string> = {
  invoiced: 'Invoiced',
}

const formatPaymentStateName = (state: string): string => {
  if (paymentStateDisplayNames[state]) return paymentStateDisplayNames[state]
  return state
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

interface PaymentStateBadgeProps {
  payment: Payment | null | undefined
}

/**
 * Payment state badge for V3 transactions.
 * Gray: initiating, calculating, data_validating
 * Blue: pending_assessment, settlement_initiated, settlement_pending
 * Yellow: settlement_outstanding, settlement_pass
 * Green: settled
 * Red: all *_failed states
 */
export const PaymentStateBadge: React.FC<PaymentStateBadgeProps> = ({ payment }) => {
  const displayState = payment?.state ? getPaymentStateStyle(payment.state)[0] : 'none'
  const defaultLabel = payment?.state ? formatPaymentStateName(payment.state) : '-'
  const label = useFormatMessage(`dashboard.transaction.payment.${displayState}`, defaultLabel)

  if (!payment?.state) {
    return <span>-</span>
  }
  const [, backgroundColor, fontColor] = getPaymentStateStyle(payment.state)
  const badge = (
    <Badge
      count={label}
      style={{ backgroundColor, color: fontColor, boxShadow: `${backgroundColor} 0px 0px 0px 0.5px` }}
    />
  )
  const errorMsg = (payment.payment_error_msg ?? payment.paymentErrorMsg)?.trim()
  if (errorMsg) return <Tooltip title={errorMsg} overlayInnerStyle={{ textAlign: 'center', maxWidth: 200 }}>{badge}</Tooltip>
  return badge
}

const getPaymentStateStyle = (state: string): [string, string, string] => {
  const { grey12, grey7, skyBlue15, carribeanCurrent, orangePeel15, warningPrimary, active, neonGreen, underRepair, dangerPrimary } = theme.colors

  // Red: all *_failed states
  if (state.endsWith('_failed')) {
    return [state, underRepair, dangerPrimary]
  }
  // Gray: voided (reviewed and closed, no further action)
  if (state === 'voided') {
    return [state, grey12, grey7]
  }
  // Gray: initial states
  if (['initiating', 'calculating', 'data_validating'].includes(state)) {
    return [state, grey12, grey7]
  }
  // Blue: active processing
  if (['pending_assessment', 'invoiced', 'settlement_initiated', 'settlement_pending'].includes(state)) {
    return [state, skyBlue15, carribeanCurrent]
  }
  // Yellow: intermediate success
  if (['settlement_outstanding', 'settlement_pass'].includes(state)) {
    return [state, orangePeel15, warningPrimary]
  }
  // Green: final success
  if (state === 'settled') {
    return [state, active, neonGreen]
  }
  // Default: gray
  return [state, grey12, grey7]
}

/**
 * * IMPORTNANT: Transaction State below in 'getStyle' function
 * Ongoing:
    no_reservation
    checked_in
  Completed:
    confirmed
    free
  Pending Payment:
    Pending_payment
  Errored:
    Errored
  Canceled:
    Canceled
    For everything else, display status as is 
 **/

const getStyle = (currentState: string): string[] => {
  const {
    active,
    neonGreen,
    orangePeel15,
    warningPrimary,
    grey12,
    black,
    underRepair,
    dangerPrimary,
    yellow,
    blue,
    skyBlue15,
    carribeanCurrent,
  } = theme.colors

  switch (currentState) {
    case 'confirmed':
    case 'completed':
    case 'free':
      return ['completed', active, neonGreen]
    case 'pending_payment':
    case 'reserved':
      return [currentState, orangePeel15, warningPrimary]
    case 'no_reservation':
    case 'ongoing':
    case 'checked_in':
      return ['ongoing', orangePeel15, warningPrimary]
    case 'canceled':
      return [currentState, grey12, black]
    case 'errored':
      return [currentState, underRepair, dangerPrimary]
    // v3 error states
    case 'failed_initiation':
    case 'failed_charging':
    case 'hardware_error':
      return [currentState, underRepair, dangerPrimary]
    case 'paid':
    case 'preauthorized':
      return [currentState, yellow]
    // v3 in-progress states
    case 'in_progress':
    case 'starting':
    case 'pending_unplug':
      return [currentState, orangePeel15, warningPrimary]
    // v3 initiation states
    case 'initiating':
    case 'verified':
      return [currentState, skyBlue15, carribeanCurrent]
    default:
      return [currentState, blue]
  }
}

/**
 * V3 Transaction State Color Mapping:
 * Completed (green): completed
 * Error states (red): failed_initiation, failed_charging, hardware_error
 * Active/ongoing (orange): in_progress, starting, pending_unplug
 * Initiation (blue): initiating, verified
 */
const getV3Style = (currentState: string): string[] => {
  const {
    active,
    neonGreen,
    orangePeel15,
    warningPrimary,
    black,
    underRepair,
    dangerPrimary,
    blue,
    skyBlue15,
    carribeanCurrent,
  } = theme.colors

  switch (currentState) {
    // Completed states - Green
    case 'completed':
      return ['completed', active, neonGreen]
    
    // Error states - Red
    case 'failed_initiation':
    case 'failed_charging':
    case 'hardware_error':
      return [currentState, underRepair, dangerPrimary]
    
    // Active/ongoing states - Orange
    case 'in_progress':
      return ['ongoing', orangePeel15, warningPrimary]
    case 'starting':
    case 'pending_unplug':
      return [currentState, orangePeel15, warningPrimary]
    
    // Initiation states - Blue
    case 'initiating':
    case 'verified':
      return [currentState, skyBlue15, carribeanCurrent]
    
    // Default fallback
    default:
      return [currentState, blue, black]
  }
}
